import { resolve } from 'aurelia';
import {
  AccountService,
  AdminService,
  AnalyticsService,
  CalendarService,
  FeedbackService,
  SavedSearchService,
  SubscriptionService
} from './services';

// Root orchestrator. All shared state and behavior now lives in singleton services that the
// region components (app-header, filter-panel, calendar-grid, releases-panel, app-footer) and
// modals consume directly via DI. This view-model only wires up the startup sequence and exposes
// the services that the root template (my-app.html) binds for top-level layout state.
export class MyApp {
  readonly calendar: CalendarService = resolve(CalendarService);
  readonly account: AccountService = resolve(AccountService);
  readonly admin: AdminService = resolve(AdminService);
  private readonly savedSearch: SavedSearchService = resolve(SavedSearchService);
  private readonly subscriptions: SubscriptionService = resolve(SubscriptionService);
  private readonly feedback: FeedbackService = resolve(FeedbackService);
  private readonly analytics: AnalyticsService = resolve(AnalyticsService);

  // Suppress the browser's right-click context menu across the whole page. Kept as a bound
  // field so the same reference can be removed in detached().
  private readonly blockContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  // Startup sequence. Order matters: establish the month bounds and locally-cached saved
  // searches first, then resolve identity (admin + account), then hydrate the signed-in user's
  // saved searches and subscriptions, sync the time zone, and finally fetch the initial month.
  async attached(): Promise<void> {
    document.addEventListener('contextmenu', this.blockContextMenu);
    // Top of the signup funnel: one visit per browser session, recorded before anything else
    // so a slow or failing API call downstream cannot hide the visit.
    this.analytics.trackOnce('visit');
    this.calendar.initMonths();
    this.savedSearch.loadSavedSearches();
    // Independent of the critical release path: load the feedback flag so the floating button
    // can appear as soon as the server answers, without blocking the initial month fetch.
    void this.feedback.loadConfig();
    await this.admin.checkAuth();
    await this.account.checkAccount();
    await this.savedSearch.initSavedSearches();
    await this.subscriptions.loadSubscriptions();
    await this.account.syncTimeZone();
    // Show the one-time email opt-in prompt for users signing in for the first time.
    this.account.openEmailOptInIfNeeded();
    // Show the first-visit welcome overlay for anonymous visitors who have not dismissed it.
    this.account.openWelcomeIfNeeded();
    await this.calendar.fetchReleases('initial');
  }

  detached(): void {
    document.removeEventListener('contextmenu', this.blockContextMenu);
  }
}
